// IND-011: bloques de Gestión de datos ordenados por uso: punto de partida
// (recuento), coherencia de la carga diaria y tablas maestras.
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DownloadRounded, UploadFileRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  downloadMissingDataWorkbook,
  getPlantDataFreshness,
  getPlantDataReview,
  getRecountStatus,
} from '../services/industrialCostService';
import type {
  IndustrialCostDataSourceStatusItem,
  MissingDataTable,
} from '../services/industrialCostService';
import { formatDataDay } from '../utils/dailyLoadRunFile';
import { loadBreakdownText } from '../utils/recountProposal';
import { IMPORT_PERMISSION_DENIED_TEXT } from '../utils/importPermission';
import { PLANT_DATA_REVIEW_QUERY_KEY } from './IndustrialCostFileReviewPanel';
import { sortRows, useTableSort } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';

export const RECOUNT_STATUS_QUERY_KEY = ['industrial-cost-recount-status'];

const PANEL_SX = {
  borderRadius: 4,
  bgcolor: 'rgba(15,23,42,0.72)',
  border: '1px solid rgba(148,163,184,0.16)',
};

function BlockTitle({ step, title, subtitle }: { step: string; title: string; subtitle: string }) {
  return (
    <Box>
      <Typography sx={{ color: '#7dd3fc', fontSize: 12, fontWeight: 950, letterSpacing: 0.8 }}>
        {step}
      </Typography>
      <Typography sx={{ color: '#f8fafc', fontSize: 18, fontWeight: 900 }}>{title}</Typography>
      <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>{subtitle}</Typography>
    </Box>
  );
}

// IND-020: Excel con lo que falta en la tabla, listo para completar y pegar.
function MissingDataButton({ table }: { table: MissingDataTable }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await downloadMissingDataWorkbook(table);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lo_que_falta_${table}.xlsx`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError('No se pudo descargar. Vuelve a intentarlo.');
    } finally {
      setDownloading(false);
    }
  };
  return (
    <Stack spacing={0.25} alignItems="flex-end">
      <Button
        size="small"
        variant="text"
        startIcon={<DownloadRounded />}
        onClick={() => void handleClick()}
        disabled={downloading}
        sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
      >
        {downloading ? 'Preparando…' : 'Descargar lo que falta'}
      </Button>
      {error ? <Typography sx={{ color: '#fca5a5', fontSize: 11 }}>{error}</Typography> : null}
    </Stack>
  );
}

function PermissionNotice({ canImport }: { canImport: boolean }) {
  return canImport ? null : (
    <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>{IMPORT_PERMISSION_DENIED_TEXT}</Typography>
  );
}

// 1. Punto de partida · Recuento físico
export function RecountStartBlock({
  canImport,
  onLoad,
}: {
  canImport: boolean;
  onLoad: () => void;
}) {
  const statusQuery = useQuery({
    queryKey: RECOUNT_STATUS_QUERY_KEY,
    queryFn: getRecountStatus,
    staleTime: 60_000,
    retry: false,
  });
  const status = statusQuery.data;
  const day = formatDataDay(status?.count_date);

  return (
    <Paper sx={{ ...PANEL_SX, border: '1px solid rgba(56,189,248,0.35)' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ p: 2.5 }}
      >
        <Stack spacing={1}>
          <BlockTitle
            step="1 · PUNTO DE PARTIDA"
            title="Recuento físico"
            subtitle={`${status?.source_file_name ?? 'RECUENTO POL 2026.xlsx'} · producción y albaranes se cuentan desde este día.`}
          />
          <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography sx={{ color: '#f8fafc', fontSize: 34, fontWeight: 950, lineHeight: 1 }}>
              {day ?? '—'}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
              {day ? 'día 1 del stock calculado' : 'Sin recuento cargado'}
            </Typography>
          </Stack>
          <Typography sx={{ color: '#cbd5e1', fontSize: 13 }}>
            {`Hoja: ${status?.sheet_name ?? '—'} · Columna: ${status?.quantity_column ?? '—'} · `}
            {`${status?.materials_count ?? 0} materiales`}
            {/* IND-018: desglose de la última carga */}
            {loadBreakdownText(status?.breakdown) ? ` (${loadBreakdownText(status?.breakdown)})` : ''}
            {status && status.materials_with_older_count > 0
              ? ` · ${status.materials_with_older_count} materiales con recuento anterior`
              : ''}
            {status?.loaded_at ? ` · cargado el ${formatLoadedAt(status.loaded_at)}` : ''}
          </Typography>
        </Stack>
        <Stack spacing={0.75} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<UploadFileRounded />}
            onClick={onLoad}
            disabled={!canImport}
            sx={{ textTransform: 'none', fontWeight: 900 }}
          >
            Cargar nuevo recuento
          </Button>
          <MissingDataButton table="recuento" />
          <PermissionNotice canImport={canImport} />
        </Stack>
      </Stack>
    </Paper>
  );
}

// 2. Línea de coherencia de la carga diaria (avisos de IND-004).
export function DailyCoherenceLine() {
  const freshnessQuery = useQuery({
    queryKey: ['industrial-cost-plant-data-freshness'],
    queryFn: getPlantDataFreshness,
    staleTime: 60_000,
    retry: false,
  });
  const warnings = freshnessQuery.data?.warnings ?? [];
  if (!freshnessQuery.data) return null;
  if (warnings.length === 0) {
    return (
      <Alert severity="success" variant="outlined">
        Los ficheros cargados son coherentes entre sí: sin desfases de fecha ni días laborables
        sin producción.
      </Alert>
    );
  }
  return (
    <Stack spacing={0.75}>
      {warnings.map((warning) => (
        <Alert
          key={warning.code}
          severity={warning.severity === 'info' ? 'info' : 'warning'}
          variant="outlined"
        >
          {warning.message}
          {/* IND-022: el aviso es del año: solo los primeros días; el resto,
              en «Revisa tus ficheros», donde se pueden marcar como festivo. */}
          {warning.dates && warning.dates.length > 0
            ? `: ${warning.dates.slice(0, 8).map((value) => formatDataDay(value)).join(', ')}` +
              (warning.dates.length > 8 ? ` y ${warning.dates.length - 8} más (ver «Revisa tus ficheros»)` : '')
            : ''}
        </Alert>
      ))}
    </Stack>
  );
}

export type MasterTableKey =
  | 'technical-consumptions'
  | 'box-customer-personalizations'
  | 'box-purchase-mappings';

const MASTER_ROWS: Array<{
  key: MasterTableKey;
  reviewFileKey: string;
  missingTable: MissingDataTable;
  label: string;
  fileName: string;
  content: string;
}> = [
  {
    key: 'technical-consumptions',
    reviewFileKey: 'escandallo',
    missingTable: 'escandallo',
    label: 'Escandallo',
    fileName: 'escandallo_final.xlsx',
    content: 'Producto → materiales, cajas y consumo por metro',
  },
  {
    key: 'box-customer-personalizations',
    reviewFileKey: 'personalizacion',
    missingTable: 'personalizaciones',
    label: 'Personalización por cliente',
    fileName: 'mapeo_clientes_personalizacion.xlsx',
    content: 'Cliente → personalización de caja',
  },
  {
    key: 'box-purchase-mappings',
    reviewFileKey: 'cajas_reales',
    missingTable: 'cajas-reales',
    label: 'Cajas reales',
    fileName: 'mapeo_cajas_reales.xlsx',
    content: 'Caja genérica + personalización → caja real',
  },
];

function formatLoadedAt(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
}

// 3. Solo cuando cambien · Tablas maestras
type MasterSortKey = 'label' | 'file' | 'loaded' | 'rows' | 'toFix';
const MASTER_COLUMNS: Array<{ key: MasterSortKey; label: string }> = [
  { key: 'label', label: 'Tabla' },
  { key: 'file', label: 'Fichero' },
  { key: 'loaded', label: 'Última carga' },
  { key: 'rows', label: 'Contenido' },
  { key: 'toFix', label: 'Filas a corregir' },
];

export function MasterTablesBlock({
  canImport,
  statusByKey,
  onLoad,
  onLoadSeveral,
}: {
  canImport: boolean;
  statusByKey: Partial<Record<string, IndustrialCostDataSourceStatusItem>>;
  onLoad: (key: MasterTableKey) => void;
  onLoadSeveral: () => void;
}) {
  const reviewQuery = useQuery({
    queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
    queryFn: getPlantDataReview,
    staleTime: 60_000,
    retry: false,
  });
  const reviewByFile = new Map(
    (reviewQuery.data?.files ?? []).map((file) => [file.file_key, file]),
  );
  // IND-026: orden por cabecera; por defecto, el orden fijo de las tablas.
  const sort = useTableSort<MasterSortKey>(null, 'asc');
  const masterRows = MASTER_ROWS.map((row) => {
    const status = statusByKey[row.key];
    const review = reviewByFile.get(row.reviewFileKey);
    // IND-012: avisos que se corrigen en este fichero.
    const ownNotices = (reviewQuery.data?.notices ?? []).filter(
      (notice) => notice.file_key === row.reviewFileKey,
    );
    const errorsCount = ownNotices.filter((notice) => notice.level !== 'info').length;
    const warningsCount = ownNotices.filter((notice) => notice.level === 'info').length;
    const toFix = review ? errorsCount + warningsCount : null;
    return { row, status, errorsCount, warningsCount, toFix };
  });
  const sortKey = sort.key;
  const visibleRows = sortKey
    ? sortRows(
        masterRows,
        (item) =>
          sortKey === 'label'
            ? item.row.label
            : sortKey === 'file'
              ? item.status?.source_file_name ?? item.row.fileName
              : sortKey === 'loaded'
                ? item.status?.effective_at
                : sortKey === 'rows'
                  ? item.status?.rows_count
                  : item.toFix,
        sort.direction,
      )
    : masterRows;

  return (
    <Paper sx={PANEL_SX}>
      <Stack spacing={1.5} sx={{ p: 2.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
        >
          <BlockTitle
            step="3 · SOLO CUANDO CAMBIEN"
            title="Tablas maestras"
            subtitle="Cada carga sustituye la tabla completa."
          />
          <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={onLoadSeveral}
              disabled={!canImport}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Cargar varias a la vez
            </Button>
            <PermissionNotice canImport={canImport} />
          </Stack>
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(148,163,184,0.16)' } }}>
            <TableHead>
              <TableRow>
                {MASTER_COLUMNS.map((column) => (
                  <SortableHeaderCell
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    sort={sort}
                    firstDirection={column.key === 'label' || column.key === 'file' ? 'asc' : 'desc'}
                    sx={{ color: '#94a3b8', fontSize: 12 }}
                  />
                ))}
                <TableCell sx={{ color: '#94a3b8', fontSize: 12 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map(({ row, status, errorsCount, warningsCount, toFix }) => {
                return (
                  <TableRow key={row.key}>
                    <TableCell sx={{ color: '#f8fafc', fontWeight: 800 }}>{row.label}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1', fontSize: 12 }}>
                      {status?.source_file_name ?? row.fileName}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1', fontSize: 12 }}>
                      {formatLoadedAt(status?.effective_at)}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1', fontSize: 12 }}>
                      {row.content}
                      {status?.rows_count != null ? ` · ${status.rows_count} filas` : ''}
                    </TableCell>
                    <TableCell>
                      {toFix == null ? (
                        <Typography sx={{ color: '#64748b', fontSize: 12 }}>—</Typography>
                      ) : toFix === 0 ? (
                        <Chip size="small" color="success" variant="outlined" label="Nada que corregir" />
                      ) : (
                        <Stack direction="row" spacing={0.5}>
                          {errorsCount > 0 ? (
                            <Chip size="small" color="error" label={`${errorsCount} a corregir`} />
                          ) : null}
                          {warningsCount > 0 ? (
                            <Chip size="small" color="info" variant="outlined" label={`${warningsCount} informativos`} />
                          ) : null}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        <MissingDataButton table={row.missingTable} />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => onLoad(row.key)}
                          disabled={!canImport}
                          sx={{ textTransform: 'none', fontWeight: 800 }}
                        >
                          Cargar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
}

export function SectionHeading({ step, title, subtitle }: { step: string; title: string; subtitle: string }) {
  return <BlockTitle step={step} title={title} subtitle={subtitle} />;
}
